/*
  Сведения организации из присланного файла - рядом с тем, что стоит в
  настройках, и кнопка записи (решение владельца 30.09.2026).

  Карточка предприятия, договор управления или перечень домов с участками -
  не данные должников, их не с чем связать по лицевому счёту. Зато в них то,
  что иначе вводится руками в настройках. Система показывает найденное рядом с
  текущим значением, а записывает человек: реквизит и срок оплаты уходят в
  каждое заявление, и молча их менять нельзя.

  Выбор по строкам (01.10.2026, проверка перед выкладкой). Раньше кнопка
  записывала всё найденное разом, и реквизит, уже стоящий в настройках,
  переписывался значением из файла - может быть, из старого договора. Теперь у
  каждой строки флажок, на сервер уходят только отмеченные (`fields`,
  `addresses`). Строка, которая МЕНЯЕТ уже заполненное, заранее не отмечена:
  перезапись - только явным выбором. Пустое в настройках и дома к привязке
  отмечены заранее.
*/

import { useState } from 'react'

import { Lock } from '../AccessContext.jsx'
import { Check, Notice, Tag } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import usePaging from '../usePaging.js'

/*
  Вердикт `address` (сервер, 01.10.2026): адрес дома из файла не прошёл правил
  привязки - нет номера дома или населённого пункта. Что именно не так, сервер
  пишет в `note`, как и у остальных вердиктов; метка выделяет такой дом среди
  привязываемых. Флажка у него нет, как у всех домов, кроме `attach`, - значит,
  и отмечен он не бывает.
*/
const ADDRESS_TAG = 'нужен полный адрес с номером дома'

/*
  Колонка флажков есть, только когда хоть одну строку можно отметить: иначе
  слева стояла пустая полоса без заголовка (осмотр 01.10.2026, «Перечень
  домов», где все дома уже привязаны).
*/

// Перезаписывает ли строка то, что уже стоит в настройках.
const overwrites = (item) => Boolean(item.current) && item.changes

function useChoice(initial) {
  const [chosen, setChosen] = useState(initial)
  const toggle = (key) =>
    setChosen((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]))
  return [chosen, toggle]
}

function Organisation({ items, busy, onApply }) {
  const writable = items.filter((item) => item.changes)
  const [chosen, toggle] = useChoice(writable.filter((item) => !overwrites(item)).map((item) => item.field))
  return (
    <>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              {writable.length ? <th className="table__control" aria-label="Записать" /> : null}
              <th>Сведение</th>
              <th>В файле</th>
              <th>Сейчас в настройках</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.field}>
                {writable.length ? (
                  <td className="table__control">
                    {item.changes ? (
                      <Check
                        checked={chosen.includes(item.field)}
                        onChange={() => toggle(item.field)}
                        title={`Записать: ${item.title}`}
                      />
                    ) : null}
                  </td>
                ) : null}
                <td>{item.title}</td>
                <td className="table__block" data-label="В файле">
                  {item.found}
                  {item.quote ? <div className="muted">«{item.quote}»</div> : null}
                </td>
                <td data-label="Сейчас в настройках">
                  {item.current ?? <span className="muted">не заполнено</span>}
                  {overwrites(item) ? <div className="muted">будет заменено, если отметить</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Lock>
        <div className="row">
          <button
            type="button"
            className="button"
            disabled={busy || !chosen.length}
            onClick={() => onApply({ organisation: true, houses: false, fields: chosen, addresses: [] })}
          >
            {writable.length ? `Записать в настройки (${chosen.length})` : 'Всё уже стоит в настройках'}
          </button>
        </div>
      </Lock>
    </>
  )
}

function Houses({ items, busy, onApply }) {
  const ready = items.filter((item) => item.verdict === 'attach')
  const [chosen, toggle] = useChoice(ready.map((item) => item.address))
  // 21.10.2026: перечень домов из файла бывает в сотни строк - общая панель страниц.
  // Листается только показ: отметки и число в кнопке считаются по ВСЕМУ перечню.
  const { rows, bar } = usePaging(items)
  return (
    <>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              {ready.length ? <th className="table__control" aria-label="Привязать" /> : null}
              <th>Адрес дома</th>
              <th>Участок</th>
              <th>Что будет</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={`${item.address}-${item.section_number}`}>
                {ready.length ? (
                  <td className="table__control">
                    {item.verdict === 'attach' ? (
                      <Check
                        checked={chosen.includes(item.address)}
                        onChange={() => toggle(item.address)}
                        title={`Привязать: ${item.address}`}
                      />
                    ) : null}
                  </td>
                ) : null}
                <td>{item.address}</td>
                <td data-label="Участок">№ {item.section_number}</td>
                <td className="table__block" data-label="Что будет">
                  {item.verdict === 'address' ? (
                    <div>
                      <Tag kind="warn">{ADDRESS_TAG}</Tag>
                    </div>
                  ) : null}
                  {item.note}
                  {item.verdict === 'taken' ? <div className="muted">Сейчас: {item.taken_by}</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="домов" />
      <Lock>
        <div className="row">
          <button
            type="button"
            className="button"
            disabled={busy || !chosen.length}
            onClick={() => onApply({ organisation: false, houses: true, fields: [], addresses: chosen })}
          >
            {ready.length ? `Привязать дома к участкам (${chosen.length})` : 'Привязывать нечего'}
          </button>
        </div>
      </Lock>
    </>
  )
}

function Applied({ applied }) {
  if (!applied) return null
  const parts = []
  if (applied.written.length) parts.push(`записано сведений: ${applied.written.length}`)
  if (applied.attached) parts.push(`привязано домов: ${applied.attached}`)
  // Сколько домов осталось без участка - иначе «Готово» читалось бы как «все
  // дома привязаны» (01.10.2026). Причина - у каждого дома в таблице выше.
  if (applied.left) parts.push(`не привязано домов: ${applied.left} - причина у каждого в таблице`)
  return (
    <Notice kind="ok">{parts.length ? `Готово - ${parts.join(', ')}.` : 'Менять было нечего.'}</Notice>
  )
}

// Ключ блока - то, что показано: после записи сервер присылает новые «сейчас в
// настройках», и отметки строятся заново по новым строкам, а не по старым.
const signature = (items, pick) => items.map(pick).join('|')

export default function FoundSettings({ file, state, onApply }) {
  const shown = state?.shown
  if (!shown && !state?.error) return null
  return (
    <section className="section card">
      <h2 className="section__title filename">Сведения организации в файле «{file.filename}»</h2>
      {shown?.organisation.length ? (
        <Organisation
          key={signature(shown.organisation, (item) => `${item.field}:${item.found}:${item.current}`)}
          items={shown.organisation}
          busy={state.busy}
          onApply={onApply}
        />
      ) : null}
      {shown?.houses.length ? (
        <Houses
          key={signature(shown.houses, (item) => `${item.address}:${item.verdict}`)}
          items={shown.houses}
          busy={state.busy}
          onApply={onApply}
        />
      ) : null}
      <Applied applied={state.applied} />
      <Notice error={state.error} />
    </section>
  )
}
